import React, { useCallback, useMemo } from 'react';
import { SubText } from '@aurora/widgets-react';
import moment from 'moment-timezone';
import styles from './TimeSlider.scss';
import clsx from 'clsx';
import ContainerDimensions from 'react-container-dimensions';
import { useTranslation } from 'react-i18next';

export interface TimeSliderProps {
  value: number;
  onChange: (val: number) => void;
  times: string[];
  variant: 'primary' | 'secondary';
}

interface Marker {
  value: number;
  label?: string;
}

const Markers: React.FunctionComponent<{ marks: Marker[]; count: number }> = ({
  marks,
  count,
}) => (
  <div className={styles.marksContainer} aria-hidden>
    {marks.map((mark, i) => {
      const style = { left: `${(100 * mark.value) / (count - 1)}%` };
      return (
        <React.Fragment key={i}>
          <span
            style={style}
            className={clsx(
              styles.markContainer,
              mark.label && styles.withLabel
            )}
          >
            <div className={styles.mark}></div>
            {mark.label ? (
              <SubText className={styles.markLabel}>{mark.label}</SubText>
            ) : null}
          </span>
        </React.Fragment>
      );
    })}
  </div>
);

const MemoizedMarkers = React.memo(Markers);

export const InnerTimeSlider: React.FunctionComponent<
  TimeSliderProps & { width: number }
> = ({ onChange, value, times, variant, width }) => {
  const { t } = useTranslation();

  const onInputChange = useCallback(
    (event) => onChange(Number(event.target.value)),
    [onChange]
  );
  const valueTexts = useMemo(
    () => times.map((t) => moment(t).format('dddd [den] D/M [klockan] HH.mm')),
    [times]
  );
  const marks = useMemo(() => {
    const ret: { value: number; label?: string }[] = [];
    const remWidth =
      width / parseFloat(getComputedStyle(document.documentElement).fontSize);
    let prevMarkDay: number = -1;
    let prevLabelMark = -100;
    // If there is less than half a rem of place for each mark, decimate the
    // markers until there is atleast 1 rem per marker.
    const markCount =
      remWidth / times.length < 0.5 ? Math.floor(remWidth / 1.0) : times.length;
    const labelDelta = Math.ceil(markCount / (remWidth / 3));
    for (let i = 0; i < markCount; ++i) {
      const index = Math.floor((i * times.length) / markCount);
      const time = moment(times[index]);
      if (prevMarkDay === time.day()) {
        ret.push({
          value: index,
        });
      } else {
        if (i - labelDelta < prevLabelMark) {
          ret.push({ value: index });
        } else {
          ret.push({
            value: index,
            label: time.format('D/M'),
          });
          prevLabelMark = i;
        }
        prevMarkDay = time.day();
      }
    }
    // very last item should always have a mark
    if (times.length % markCount !== 0) {
      const index = times.length - 1;
      const time = moment(times[index]);
      if (
        prevMarkDay === time.day() ||
        prevLabelMark >= markCount - labelDelta
      ) {
        ret.push({
          value: index,
        });
      } else {
        ret.push({
          value: index,
          label: time.format('D/M'),
        });
      }
    }
    return ret;
  }, [times, width]);
  return (
    <div className={clsx(styles.timeSlider, styles[variant])}>
      <MemoizedMarkers marks={marks} count={times.length} />
      <input
        type="range"
        className={styles.inputElement}
        min={0}
        max={times.length - 1}
        aria-label={t('selectedPointInTime')}
        aria-valuetext={valueTexts[value]}
        value={value}
        onChange={onInputChange}
      />
    </div>
  );
};

export const TimeSlider: React.FunctionComponent<TimeSliderProps> = (props) => {
  return (
    <ContainerDimensions>
      {({ width }) => <InnerTimeSlider width={width} {...props} />}
    </ContainerDimensions>
  );
};
